import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SEO from "../components/SEO";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Calculator, Plus, Trash2, ArrowRight, Info } from "lucide-react";
import ShippingInfoModal from "../components/ShippingInfoModal";
import useToolPageCms from "../hooks/useToolPageCms";
import { ToolPageBanner, ToolPageCtaBand } from "../components/ToolPageCms";
import { dimensionMetrics, aggregateMeasurements } from "../utils/shipmentMeasurements";

const FALLBACK_CMS = {
  page_banner: {
    eyebrow: "CBM Calculator",
    title: "CBM Calculator —",
    highlight: "bereken in seconden",
    subtitle:
      "Bereken volume (m³), volumetrisch gewicht én belastbaar gewicht (whatever is greater) per regel.",
    badges: [
      { icon: "package", label: "Per regel berekenen" },
      { icon: "info",    label: "Werkelijk + volumetric" },
      { icon: "support", label: "Direct offerte aanvragen" },
    ],
  },
  intro: {},
  cta_band: {
    title: "Klaar voor een offerte?",
    description: "Vraag binnen 1 werkdag een prijs op basis van uw CBM-berekening.",
    cta_primary_label: "Vraag offerte aan",
    cta_primary_url: "/contact?get-quote=1#quote-form",
    cta_secondary_label: "Bel ons",
    cta_secondary_url: "tel:+31488235999",
  },
};

const UNITS = [
  { key: "cm",   label: "cm",   toMeter: 0.01 },
  { key: "m",    label: "m",    toMeter: 1.0 },
  { key: "inch", label: "inch", toMeter: 0.0254 },
  { key: "ft",   label: "ft",   toMeter: 0.3048 },
];

const EMPTY = {
  description: "",
  quantity: 1,
  length: "",
  width: "",
  height: "",
  unit: "cm",
  gross_weight_kg: "",
};

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function lineCbm(row) {
  const { cbm } = dimensionMetrics(row);
  return Math.round(cbm * 10000) / 10000;
}

function lineVolAirWeight(row) {
  const kg = dimensionMetrics(row).volumetric;
  if (!kg) return null;
  return Math.round(kg * 100) / 100;
}

function lineComplete(row) {
  return (
    num(row.length) > 0 &&
    num(row.width) > 0 &&
    num(row.height) > 0 &&
    num(row.quantity) > 0 &&
    num(row.gross_weight_kg) > 0
  );
}

export default function CbmCalculatorPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([{ ...EMPTY }]);
  const [showInfo, setShowInfo] = useState(false);
  const { data: cms } = useToolPageCms("cbm-calculator", FALLBACK_CMS);

  const setRow = (idx, patch) => setRows((arr) => arr.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const addRow = () => setRows((arr) => [...arr, { ...EMPTY }]);
  const removeRow = (idx) => setRows((arr) => (arr.length === 1 ? arr : arr.filter((_, i) => i !== idx)));

  const totals = useMemo(() => {
    // Public input weight is per ROW, unlike warehouse weight per PIECE.
    const measured = aggregateMeasurements(rows.map((row) => ({ ...row,
      weightPerPiece: num(row.quantity) > 0 ? num(row.gross_weight_kg) / num(row.quantity) : 0,
    })));
    const { cbm, pieces: qty, weight: gross, volumetric: vol, chargeable } = measured;
    let allComplete = true;
    for (const r of rows) {
      if (!lineComplete(r)) allComplete = false;
    }
    const basis = chargeable === vol && vol >= gross ? "volumetric_weight" : "gross_weight";
    return {
      totalCbm: Math.round(cbm * 10000) / 10000,
      totalQty: qty,
      totalGrossKg: Math.round(gross * 100) / 100,
      totalVolKg: Math.round(vol * 100) / 100,
      chargeable: Math.round(chargeable * 100) / 100,
      basis,
      allComplete,
    };
  }, [rows]);

  const goQuote = () => {
    // Persist a structured prefill payload so the offerteformulier can
    // automatically fill the dims/CBM block without ugly URL params.
    // Always store the first complete row's measurements (the form
    // accepts a single row today). The full multi-row summary is also
    // dropped into the description so customers see context.
    const completeRow =
      rows.find((r) => lineComplete(r)) ||
      rows.find((r) => num(r.length) > 0 && num(r.width) > 0 && num(r.height) > 0) ||
      rows[0];
    const summary = rows
      .filter((r) => r.description || r.length)
      .map(
        (r, i) =>
          `Regel ${i + 1}: ${r.quantity}× ${r.length}×${r.width}×${r.height} ${r.unit} (${r.gross_weight_kg || "?"} kg)` +
          (r.description ? ` — ${r.description}` : "") +
          ` → ${lineCbm(r)} m³ / vol-air ${lineVolAirWeight(r) ?? "?"} kg`,
      )
      .join(" | ");
    const note =
      `CBM Calculator: ${totals.totalCbm} m³ (${totals.totalQty} items). ` +
      `Werkelijk gewicht: ${totals.totalGrossKg} kg. ` +
      `Volumetrisch gewicht: ${totals.totalVolKg} kg. ` +
      `Belastbaar gewicht: ${totals.chargeable} kg ` +
      `(${totals.basis === "volumetric_weight" ? "volumetric weight is leidend" : "gross weight is leidend"}). ` +
      summary;

    // Convert the row to cm so the offerteformulier (which always
    // expects centimetres) can drop it straight into the inputs.
    let length_cm = "";
    let width_cm = "";
    let height_cm = "";
    if (completeRow) {
      const u = UNITS.find((x) => x.key === completeRow.unit) || UNITS[0];
      const toCm = u.toMeter * 100;
      length_cm = num(completeRow.length) > 0 ? Math.round(num(completeRow.length) * toCm * 100) / 100 : "";
      width_cm = num(completeRow.width) > 0 ? Math.round(num(completeRow.width) * toCm * 100) / 100 : "";
      height_cm = num(completeRow.height) > 0 ? Math.round(num(completeRow.height) * toCm * 100) / 100 : "";
    }

    const prefill = {
      source: "cbm_calculator",
      created_at: new Date().toISOString(),
      // primary row used to fill the form
      quantity: num(completeRow?.quantity) || totals.totalQty || 1,
      length_cm,
      width_cm,
      height_cm,
      weight_kg: num(completeRow?.gross_weight_kg) || totals.totalGrossKg || "",
      // totals shown as context inside the form
      total_cbm: totals.totalCbm,
      total_qty: totals.totalQty,
      total_gross_kg: totals.totalGrossKg,
      total_vol_kg: totals.totalVolKg,
      chargeable_kg: totals.chargeable,
      chargeable_basis: totals.basis,
      description: note,
    };
    try {
      window.sessionStorage.setItem(
        "quote_prefill_payload",
        JSON.stringify(prefill),
      );
    } catch {
      // sessionStorage may be unavailable (private mode, iframe with
      // restricted storage). Fall back silently — the note in the URL
      // still carries the human-readable summary.
    }
    navigate(`/contact?get-quote=1&prefill=cbm#quote-form`);
  };

  return (
    <>
      <SEO
        title="CBM Calculator voor Zeevracht — Bereken m³ + belastbaar gewicht | 5999Cargo"
        description="Bereken eenvoudig het volume in kubieke meters (CBM) en het belastbaar gewicht van uw zending voor zeevracht, pallets, dozen en verhuisgoederen naar Curaçao, Aruba en de Caribbean."
        keywords="CBM calculator, volumetric weight, chargeable weight, whatever is greater, kubieke meters berekenen, m3 zending Curaçao"
      />
      <main className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50" data-testid="cbm-calculator-page">
        <ToolPageBanner
          banner={cms?.page_banner || FALLBACK_CMS.page_banner}
          image="https://images.unsplash.com/photo-1705589244471-c1db16609ece?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
          imageAlt="Containerschip — CBM en volumetrisch gewicht berekenen"
          eyebrowIcon={Calculator}
          testid="cbm-calculator-hero"
          extras={
            <button
              type="button"
              onClick={() => setShowInfo(true)}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-white border-b border-white/40 pb-0.5 hover:border-white"
              data-testid="cbm-open-shipping-info"
            >
              <Info className="w-3.5 h-3.5" /> Belangrijke verzendinformatie bekijken
            </button>
          }
        />

        <section className="py-10">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-3">
              {rows.map((r, idx) => {
                const cbm = lineCbm(r);
                const vol = lineVolAirWeight(r);
                const complete = lineComplete(r);
                return (
                  <div key={idx} className="card-premium p-5" data-testid={`cbm-row-${idx}`}>
                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end">
                      <div className="sm:col-span-12 md:col-span-3">
                        <Label className="text-[10px] uppercase">Omschrijving</Label>
                        <Input
                          data-testid={`cbm-desc-${idx}`}
                          value={r.description}
                          onChange={(e) => setRow(idx, { description: e.target.value })}
                          className="rounded-xl w-full"
                          placeholder="bv. doos elektronica"
                        />
                      </div>
                      <div className="sm:col-span-6 md:col-span-1">
                        <Label className="text-[10px] uppercase">Aantal *</Label>
                        <Input
                          data-testid={`cbm-qty-${idx}`}
                          type="number"
                          min="1"
                          value={r.quantity}
                          onChange={(e) => setRow(idx, { quantity: e.target.value })}
                          className="rounded-xl w-full"
                        />
                      </div>
                      <div className="sm:col-span-6 md:col-span-2">
                        <Label className="text-[10px] uppercase">Lengte *</Label>
                        <Input
                          data-testid={`cbm-len-${idx}`}
                          type="number"
                          step="0.01"
                          min="0"
                          value={r.length}
                          onChange={(e) => setRow(idx, { length: e.target.value })}
                          className="rounded-xl w-full"
                        />
                      </div>
                      <div className="sm:col-span-6 md:col-span-2">
                        <Label className="text-[10px] uppercase">Breedte *</Label>
                        <Input
                          data-testid={`cbm-wid-${idx}`}
                          type="number"
                          step="0.01"
                          min="0"
                          value={r.width}
                          onChange={(e) => setRow(idx, { width: e.target.value })}
                          className="rounded-xl w-full"
                        />
                      </div>
                      <div className="sm:col-span-6 md:col-span-2">
                        <Label className="text-[10px] uppercase">Hoogte *</Label>
                        <Input
                          data-testid={`cbm-hgt-${idx}`}
                          type="number"
                          step="0.01"
                          min="0"
                          value={r.height}
                          onChange={(e) => setRow(idx, { height: e.target.value })}
                          className="rounded-xl w-full"
                        />
                      </div>
                      <div className="sm:col-span-6 md:col-span-1">
                        <Label className="text-[10px] uppercase">Eenheid</Label>
                        <select
                          data-testid={`cbm-unit-${idx}`}
                          value={r.unit}
                          onChange={(e) => setRow(idx, { unit: e.target.value })}
                          className="w-full rounded-xl border border-gray-300 bg-white px-2 py-2 text-xs h-10"
                        >
                          {UNITS.map((u) => (
                            <option key={u.key} value={u.key}>
                              {u.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="sm:col-span-6 md:col-span-1 flex md:justify-end">
                        <Button
                          type="button"
                          size="icon"
                          variant="outline"
                          className="rounded-xl text-red-600 h-10 w-10"
                          onClick={() => removeRow(idx)}
                          disabled={rows.length === 1}
                          data-testid={`cbm-row-remove-${idx}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="sm:col-span-12 md:col-span-3">
                        <Label className="text-[10px] uppercase">Werkelijk gewicht (kg) *</Label>
                        <Input
                          data-testid={`cbm-grossw-${idx}`}
                          type="number"
                          step="0.01"
                          min="0"
                          value={r.gross_weight_kg}
                          onChange={(e) => setRow(idx, { gross_weight_kg: e.target.value })}
                          className="rounded-xl w-full"
                          placeholder="bv. 18.5"
                        />
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-gray-600">
                      <span>
                        Volume: <span className="font-mono font-bold text-[#082c50]" data-testid={`cbm-row-result-${idx}`}>{cbm} m³</span>
                      </span>
                      {vol !== null && (
                        <span>
                          Volumetric weight: <span className="font-mono font-bold text-[#082c50]" data-testid={`cbm-row-volw-${idx}`}>{vol} kg</span>
                        </span>
                      )}
                      {!complete && (
                        <span className="text-amber-700">Vul lengte, breedte, hoogte, aantal en werkelijk gewicht in.</span>
                      )}
                    </div>
                  </div>
                );
              })}
              <Button type="button" onClick={addRow} variant="outline" className="rounded-xl mt-1" data-testid="cbm-add-row">
                <Plus className="w-4 h-4 mr-1" /> Regel toevoegen
              </Button>
            </div>

            <aside className="card-dark p-7 lg:sticky lg:top-24 self-start relative overflow-hidden" data-testid="cbm-result-panel">
              <div className="relative">
              <div className="text-[11px] uppercase tracking-[0.18em] text-brand-300 font-chivo font-bold">Totaal</div>
              <div className="font-chivo font-black text-5xl mt-3 font-mono-num" data-testid="cbm-total-cbm">{totals.totalCbm} m³</div>
              <div className="text-sm text-white/75 mt-1.5" data-testid="cbm-total-items">{totals.totalQty} items</div>

              <dl className="mt-6 text-xs text-white/85 space-y-2">
                <div className="flex justify-between">
                  <dt>Werkelijk gewicht</dt>
                  <dd className="font-mono-num font-bold" data-testid="cbm-total-gross">{totals.totalGrossKg} kg</dd>
                </div>
                <div className="flex justify-between">
                  <dt>Volumetric weight</dt>
                  <dd className="font-mono-num font-bold" data-testid="cbm-total-vol">{totals.totalVolKg} kg</dd>
                </div>
                <div className="flex justify-between border-t border-white/15 pt-3 mt-2">
                  <dt className="font-bold">Belastbaar gewicht</dt>
                  <dd className="font-mono-num font-bold text-brand-200" data-testid="cbm-total-chargeable">{totals.chargeable} kg</dd>
                </div>
                <div className="text-[11px] text-white/70 mt-1.5" data-testid="cbm-chargeable-basis">
                  {totals.chargeable > 0
                    ? totals.basis === "volumetric_weight"
                      ? "Volumetrisch gewicht is leidend (whatever is greater)."
                      : "Werkelijk gewicht is leidend (whatever is greater)."
                    : "Vul gewicht in om het belastbaar gewicht te bepalen."}
                </div>
              </dl>

              <Button
                onClick={goQuote}
                disabled={!totals.allComplete}
                className="mt-7 w-full rounded-xl bg-white text-brand-900 hover:bg-brand-50 font-chivo font-bold uppercase text-sm disabled:bg-white/20 disabled:text-white/50 shadow-brand-card transition-all"
                data-testid="cbm-cta-quote"
              >
                Vraag offerte aan <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <p className="text-[11px] text-white/60 mt-4 leading-relaxed">
                Wij rekenen met het hoogste gewicht: het werkelijke gewicht of het volumetrische gewicht. Dit heet &apos;whatever is greater&apos;.
              </p>
              <p className="text-[11px] text-white/50 mt-2 leading-relaxed">
                CBM = L × B × H × aantal ÷ 1.000.000 (cm). Volumetric = L × B × H ÷ 6000 × aantal.
              </p>
              </div>
            </aside>
          </div>
        </section>

        <ToolPageCtaBand cta={cms?.cta_band || FALLBACK_CMS.cta_band} testid="cbm-cta-band" />
        <ShippingInfoModal open={showInfo} onClose={() => setShowInfo(false)} alwaysOpen />
      </main>
    </>
  );
}
